import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CsrRegister, { listCount } from '../CsrRegister';
import { CsrInlineError, UnderReviewNotice } from '../CsrStatus';
import { useCsrPagedList } from '../../../hooks/useCsrDashboard';
import { fetchCsrFarmers, fetchCsrSoilTest, fetchCsrSoilTests } from '../../../services/dashboardApi';
import { DASH, errorMessage, fmtDate, fmtDecimal, initials, orDash, titleCase } from '../csrFormat';
import { fixFirst } from '../csrSoilParameters';

const COLUMNS = [
  { key: 'latest_sample_id', label: 'Sample ID', width: '130px', className: 'csr-register__id', render: (r) => orDash(r.latest_sample_id) },
  { key: 'district', label: 'District', width: 'minmax(110px, 1fr)', render: (r) => orDash(r.district) },
  { key: 'village', label: 'Village', width: 'minmax(120px, 1fr)', render: (r) => orDash(r.village) },
  { key: 'latest_crop', label: 'Crop', width: '110px', render: (r) => orDash(titleCase(r.latest_crop)) },
  { key: 'acres', label: 'Acres', width: '80px', render: (r) => (r.acres ? fmtDecimal(r.acres) : DASH) },
  { key: 'score', label: 'Soil score', width: '100px', render: () => DASH },
];

// The Sample ID column is the latest soil sample booked under each farmer; a row opens that
// report, or the farmer record when no sample has been booked yet.
const FarmersTab = ({ adminId, state, scopeName, onOpenTest, onOpenFarmer }) => {
  const list = useCsrPagedList(fetchCsrFarmers, adminId, { state });
  return (
    <CsrRegister
      id="csr-farmers"
      title={`Farmers reached · ${listCount(list)}`}
      subtitle={`Every farmer registered in ${scopeName}${list.query ? `, matching “${list.query}”` : ''}. Click a row to open the latest soil test.`}
      placeholder="Sample ID, district, village, crop or Soil Didi"
      list={list}
      columns={COLUMNS}
      rowKey={(r) => r.user_id}
      rowLabel={(r) => (r.latest_sample_id ? `Open soil test report ${r.latest_sample_id}` : `Open farmer record ${r.user_id}`)}
      onRowClick={(r) => (r.latest_sample_id ? onOpenTest(r.latest_sample_id) : onOpenFarmer(r.user_id))}
    />
  );
};

// Opened from every tab, so CsrFarmers renders it; it lives here with the farmers it describes.
// Farmers are identified by ID and sample IDs only; no names or phone numbers are loaded.
const loadRecord = async (adminId, userId) => {
  const [farmers, tests] = await Promise.all([
    fetchCsrFarmers(adminId, { search: userId, limit: 50 }),
    fetchCsrSoilTests(adminId, { user_id: userId, limit: 50 }),
  ]);
  const testRows = tests?.rows || [];
  // Inactive farmers are not in the farmers list; fall back to their test history.
  const farmer = (farmers?.rows || []).find((f) => f.user_id === userId) || (testRows[0] && {
    user_id: userId,
    state: testRows[0].state,
    district: testRows[0].district,
    village: testRows[0].village,
    latest_sample_id: testRows[0].sample_id,
    latest_crop: testRows[0].crop,
    field_officer_id: testRows[0].field_officer_id,
    field_officer_name: testRows[0].field_officer_name,
    first_tested_on: testRows[testRows.length - 1].tested_on,
    acres: null,
  });
  if (!farmer) {
    const err = new Error('not found');
    err.response = { status: 404 };
    throw err;
  }
  const latest = farmer.latest_sample_id ? (await fetchCsrSoilTest(adminId, farmer.latest_sample_id))?.test : null;
  return { farmer, tests: testRows, latest };
};

const PhotoTile = ({ label }) => (
  <figure className="csr-photo">
    <div><span>{label}</span><span>Not available yet</span></div>
  </figure>
);

export const FarmerRecordModal = ({ adminId, userId, score, onClose, onOpenTest }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) return undefined;
    let ignore = false;
    setState({ status: 'loading', data: null, error: '' });
    loadRecord(adminId, userId)
      .then((data) => { if (!ignore) setState({ status: 'success', data, error: '' }); })
      .catch((err) => { if (!ignore) setState({ status: 'error', data: null, error: errorMessage(err, 'Could not load this farmer record.') }); });
    return () => { ignore = true; };
  }, [adminId, userId, attempt]);

  const f = state.data?.farmer;
  const latest = state.data?.latest;
  const problems = latest?.parameters ? fixFirst(latest.parameters) : [];

  return (
    <Modal show={Boolean(userId)} onHide={onClose} centered size="lg" dialogClassName="csr-modal" contentClassName="csr-modal__content" aria-labelledby="csr-fpop-h">
      <div className="csr-modal__head">
        <div className="d-flex flex-column" style={{ gap: 2 }}>
          <span className="csr-kpi__label">{`Farmer record · ${orDash(f?.state)}`}</span>
          <h2 id="csr-fpop-h">{f?.latest_sample_id || (f ? 'No soil test yet' : userId)}</h2>
          {f ? <span className="csr-row__sub">{`Farmer ${f.user_id} · ${[f.village, f.district && `${f.district} district`].filter(Boolean).join(', ') || DASH}`}</span> : null}
        </div>
        <button type="button" className="csr-district__close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="csr-modal__body">
        {state.status === 'loading' ? <div className="csr-skel" style={{ height: 320 }} aria-busy="true" aria-label="Loading farmer record" /> : null}
        {state.status === 'error' ? <CsrInlineError message={state.error} onRetry={() => setAttempt((a) => a + 1)} /> : null}
        {state.status === 'success' && f ? (
          <div className="d-flex flex-column" style={{ gap: 14 }}>
            <div className="csr-photos">
              <PhotoTile label="Field photo" />
              <PhotoTile label={`Crop photo · ${orDash(titleCase(f.latest_crop))}`} />
              <PhotoTile label="Plot from satellite" />
            </div>

            <div className="csr-modal__facts">
              <div className="csr-district__cell csr-modal__didi">
                <span className="csr-didi__ini" aria-hidden="true">{f.field_officer_name ? initials(f.field_officer_name) : DASH}</span>
                <div className="d-flex flex-column">
                  <span>Tested by Soil Didi</span>
                  <strong>{orDash(f.field_officer_name)}</strong>
                  {f.field_officer_id ? <span>{f.field_officer_id}</span> : null}
                </div>
              </div>
              <div className="csr-district__cell"><span>Crop · plot size</span><strong>{`${orDash(titleCase(f.latest_crop))} · ${f.acres ? `${fmtDecimal(f.acres)} acres` : DASH}`}</strong></div>
              <div className="csr-district__cell"><span>First test · retest</span><strong>{`${fmtDate(f.first_tested_on)} · ${DASH}`}</strong></div>
            </div>

            <div className="csr-modal__split">
              <div className="csr-district">
                <strong>Soil health score</strong>
                <div className="csr-modal__score">
                  <span>Before</span><span className="csr-bar__track"><span className="csr-bar__fill" style={{ width: `${score.before}%`, background: '#8B5E3B' }} /></span><strong>{score.before}</strong>
                  <span>After</span><span className="csr-bar__track"><span className="csr-bar__fill" style={{ width: `${score.after}%`, background: '#145A32' }} /></span><strong>{score.after}</strong>
                </div>
                <span className="csr-row__sub">Score change is not available until retests are recorded.</span>
              </div>
              <div className="csr-district">
                <strong>What the soil test found · advice given</strong>
                {!latest ? <span className="csr-row__sub">No soil test booked yet.</span> : null}
                {latest?.under_review ? <UnderReviewNotice /> : null}
                {latest && !latest.parameters ? <span className="csr-row__sub">Not tested — the latest sample has no soil test report yet.</span> : null}
                {latest?.parameters && !problems.length ? <span className="csr-row__sub">All rated parameters are in the ideal range.</span> : null}
                {problems.map((pq) => (
                  <div key={pq.key} className="d-flex flex-column">
                    <strong>{`${pq.label} — ${pq.status.toLowerCase()}`}</strong>
                    <span className="csr-row__sub">{`Advised: ${DASH}`}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
              <span className="csr-row__sub">Soil test reports:</span>
              {state.data.tests.length ? state.data.tests.map((t) => (
                <button key={t.sample_id} type="button" className="csr-btn csr-btn--ghost" onClick={() => onOpenTest(t.sample_id)}>{`${t.sample_id} · ${fmtDate(t.tested_on)} →`}</button>
              )) : <span>{DASH}</span>}
            </div>
            <p className="csr-row__sub">Farmers are shown by ID only; names and phone numbers stay in Soil Doctor’s records. Photos are placeholders until field uploads are connected.</p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default FarmersTab;
